import React, { useEffect, useState, useMemo, useCallback, useReducer, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES, QUIZ_RUBRIC_PASS_POINTS } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { rubricPointsToScoreDelta } from "../../utils/quizScoring";
import { getRandomEstasseguroAvatar } from "../../utils/estasseguroAvatar";
import { setGroup, updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
	modalConfirm,
	modalQuestionResolution,
	modalHint,
	modalHintShow,
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { getRandomAciertoAvatar, getRandomErrorAvatar } from "../../utils/quizResultAvatars";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import HintMarkPista, { hintMarkPistaHtml } from "../../components/HintMarkPista";
import imageHintUsed from "../../assets/btnPistaOrange.png";
import unIntento from "../../assets/un-intento.png";
import dosIntentos from "../../assets/dos-intentos.png";
import "../StartGame.css";
import "./Scenario2.css";
import "../Scenario3/Scenario3.css";

const SC2_SWAL_POPUP_CLASS = "modal-sg5 modal-sg5--fepol";
const SC2_HINT_THEME_CLASS = `${SC2_SWAL_POPUP_CLASS} modal-sg5--hint-sc2`.trim();
const SC2_HINT_MARK_HTML = hintMarkPistaHtml();

/** Esta prueba usa −20 pt al marcador si se usa la pista (alineado con escenario 3). */
const SC2_QUIZ_HINT_COST_POINTS = 20;

const QUESTION_STEM = "Ordena las siguientes actuaciones según la prioridad operativa más adecuada.";

const RETROALIMENTACION =
	"El orden adecuado es: (1) mantener separadas a las partes; (2) controlar el entorno (p. ej. curiosos próximos); (3) obtener versiones de los implicados; (4) localizar posibles testigos.";

const HINT_HTML = `<div class="txtTexto1 mb-2">Prioriza la seguridad y el control de la escena antes de indagar a fondo: primero separar y dominar el perímetro, después información ordenada.</div>`;

/** Orden correcto de prioridad (slots de arriba a abajo / 1 → 4). */
const PRIORITY_ROWS = [
	{ slotLabel: "1", activityId: "separate", text: "Mantener separadas a las partes." },
	{ slotLabel: "2", activityId: "curious", text: "Controlar a los curiosos más próximos." },
	{ slotLabel: "3", activityId: "versions", text: "Obtener versiones de los implicados." },
	{ slotLabel: "4", activityId: "witnesses", text: "Localizar posibles testigos." },
];

const ACTIVITY_TEXT = Object.fromEntries(PRIORITY_ROWS.map((r) => [r.activityId, r.text]));

const WEIGHTED_MAX_POINTS = 100;
const WEIGHTED_MAX_GAME_DELTA = 10;
const NUMBER_ATTEMPTS = 2;

function initialPoolIds() {
	const items = PRIORITY_ROWS.map((r) => ({ id: r.activityId, text: r.text }));
	return shuffleQuizOptions(items).map((o) => o.id);
}

function isOrderCorrect(slots) {
	return PRIORITY_ROWS.every((row, i) => slots[i] === row.activityId);
}

function boardReducer(state, action) {
	const n = PRIORITY_ROWS.length;
	switch (action.type) {
		case "RESET":
			return {
				pool: initialPoolIds(),
				slots: Array(n).fill(null),
			};
		case "POOL_TO_SLOT": {
			const { slotIndex, activityId } = action;
			if (slotIndex < 0 || slotIndex >= n) return state;
			if (!state.pool.includes(activityId)) return state;
			const nextSlots = [...state.slots];
			const displaced = nextSlots[slotIndex];
			nextSlots[slotIndex] = activityId;
			let nextPool = state.pool.filter((id) => id !== activityId);
			if (displaced && !nextPool.includes(displaced)) nextPool = [...nextPool, displaced];
			return { pool: nextPool, slots: nextSlots };
		}
		case "SLOT_TO_POOL": {
			const { slotIndex, activityId } = action;
			if (slotIndex < 0 || slotIndex >= n) return state;
			if (state.slots[slotIndex] !== activityId) return state;
			const nextSlots = [...state.slots];
			nextSlots[slotIndex] = null;
			const nextPool = state.pool.includes(activityId) ? state.pool : [...state.pool, activityId];
			return { pool: nextPool, slots: nextSlots };
		}
		case "SLOT_SWAP": {
			const { fromIdx, toIdx } = action;
			if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0 || fromIdx >= n || toIdx >= n) return state;
			const nextSlots = [...state.slots];
			const tmp = nextSlots[fromIdx];
			nextSlots[fromIdx] = nextSlots[toIdx];
			nextSlots[toIdx] = tmp;
			return { ...state, slots: nextSlots };
		}
		default:
			return state;
	}
}

function Scenario2Quiz2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.SCENARIO2_PRIORITY_ORDER_QUIZ;
	const SCENARIO_ID = SCENARIOS.SCENARIO_2;

	const [board, dispatchBoard] = useReducer(boardReducer, undefined, () => ({
		pool: initialPoolIds(),
		slots: Array(PRIORITY_ROWS.length).fill(null),
	}));
	const { pool, slots } = board;

	const [attempt, setAttempt] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [seededBaseScore, setSeededBaseScore] = useState(false);
	const capitalAtQuizStartRef = useRef(null);

	const dndLocked = isCompleted || isSubmitting;

	useEffect(() => {
		if (seededBaseScore) return;
		if (!group) return;
		const s = Number(group?.score);
		if (!Number.isFinite(s)) return;
		if (capitalAtQuizStartRef.current === null) capitalAtQuizStartRef.current = s;
		setPendingScore(s);
		setSeededBaseScore(true);
	}, [group, seededBaseScore]);

	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(RETROALIMENTACION, "orange");

	const allSlotsFilled = useMemo(() => slots.every(Boolean), [slots]);

	const dragSessionRef = useRef(null);
	const ghostElRef = useRef(null);
	const [dragGhost, setDragGhost] = useState(null);

	const positionGhostFromSession = useCallback(() => {
		const el = ghostElRef.current;
		const d = dragSessionRef.current;
		if (!el || !d?.ghostShown) return;
		const maxW = typeof window !== "undefined" ? Math.min(d.width, window.innerWidth * 0.92) : d.width;
		el.style.width = `${maxW}px`;
		el.style.minHeight = `${Math.max(d.height, 28)}px`;
		el.style.left = `${d.lastClientX - d.offX}px`;
		el.style.top = `${d.lastClientY - d.offY}px`;
	}, []);

	const endPointerDrag = useCallback((el, pointerId) => {
		try {
			if (el?.hasPointerCapture?.(pointerId)) el.releasePointerCapture(pointerId);
		} catch {
			/* ignore */
		}
		dragSessionRef.current = null;
		setDragGhost(null);
		document.body.classList.remove("sc3-chrono-dnd-active");
	}, []);

	const applyDropFromPayload = useCallback((payload, target) => {
		if (target.kind === "pool") {
			if (payload.source !== "slot") return;
			dispatchBoard({
				type: "SLOT_TO_POOL",
				slotIndex: payload.slotIndex,
				activityId: payload.activityId,
			});
			return;
		}
		const slotIndex = target.slotIndex;
		if (payload.source === "pool") {
			dispatchBoard({ type: "POOL_TO_SLOT", slotIndex, activityId: payload.activityId });
		} else if (payload.source === "slot") {
			dispatchBoard({ type: "SLOT_SWAP", fromIdx: payload.slotIndex, toIdx: slotIndex });
		}
	}, []);

	const resolveDropTarget = useCallback((clientX, clientY) => {
		const stack = document.elementsFromPoint(clientX, clientY);
		for (const node of stack) {
			if (!(node instanceof Element)) continue;
			if (node.closest(".sc3-chrono-drag-ghost")) continue;
			const poolEl = node.closest('[data-sc3-drop="pool"]');
			if (poolEl) return { kind: "pool" };
			const row = node.closest("[data-sc3-slot]");
			if (row) {
				const raw = row.getAttribute("data-sc3-slot");
				const slotIndex = raw != null ? parseInt(raw, 10) : NaN;
				if (Number.isFinite(slotIndex)) return { kind: "slot", slotIndex };
			}
		}
		return null;
	}, []);

	const handleChipPointerDown = useCallback(
		(e, payload) => {
			if (dndLocked) return;
			if (e.pointerType === "mouse" && e.button !== 0) return;
			const rect = e.currentTarget.getBoundingClientRect();
			dragSessionRef.current = {
				payload,
				startX: e.clientX,
				startY: e.clientY,
				pointerId: e.pointerId,
				moved: false,
				ghostShown: false,
				offX: e.clientX - rect.left,
				offY: e.clientY - rect.top,
				width: rect.width,
				height: rect.height,
				lastClientX: e.clientX,
				lastClientY: e.clientY,
			};
			e.currentTarget.setPointerCapture(e.pointerId);
			e.preventDefault();
		},
		[dndLocked]
	);

	const handleChipPointerMove = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			d.lastClientX = e.clientX;
			d.lastClientY = e.clientY;
			const dx = e.clientX - d.startX;
			const dy = e.clientY - d.startY;
			if (dx * dx + dy * dy > 36) {
				d.moved = true;
				document.body.classList.add("sc3-chrono-dnd-active");
				if (!d.ghostShown) {
					d.ghostShown = true;
					const aid = d.payload.activityId;
					setDragGhost({
						activityId: aid,
						label: ACTIVITY_TEXT[aid] ?? aid,
						width: d.width,
						height: d.height,
					});
				}
			}
			if (d.ghostShown && ghostElRef.current) positionGhostFromSession();
		},
		[positionGhostFromSession]
	);

	useLayoutEffect(() => {
		if (!dragGhost) return;
		positionGhostFromSession();
	}, [dragGhost, positionGhostFromSession]);

	const handleChipPointerUpOrCancel = useCallback(
		(e) => {
			const d = dragSessionRef.current;
			if (!d || d.pointerId !== e.pointerId) return;
			const el = e.currentTarget;
			const { payload, moved } = d;

			if (moved) {
				const target = resolveDropTarget(e.clientX, e.clientY);
				if (target) applyDropFromPayload(payload, target);
			}

			endPointerDrag(el, e.pointerId);
		},
		[applyDropFromPayload, endPointerDrag, resolveDropTarget]
	);

	useEffect(() => {
		return () => {
			document.body.classList.remove("sc3-chrono-dnd-active");
		};
	}, []);

	async function submitAnswer(finalScore, correct, extra = {}) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const base = capitalAtQuizStartRef.current;
			const capitalInicio = Number.isFinite(base) ? base : 0;
			const deltaParaApi = finalScore - capitalInicio;

			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: deltaParaApi,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO2_FIN,
				correct,
				...extra,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch(setGroup(serverGroup));
				}

				setIsCompleted(true);

				navigate(ROUTES.SCENARIO2_FIN);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, puedes continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				});

				navigate(ROUTES.SCENARIO2_FIN);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});

			setIsSubmitting(false);
		}
	}

	const useHint = async () => {
		if (hintUsed) {
			await modalHintShow({
				hint: HINT_HTML,
				hintMarkHtml: SC2_HINT_MARK_HTML,
				color: "orange",
				big: false,
				themePopupClass: SC2_HINT_THEME_CLASS,
			});
			return;
		}

		const { used } = await modalHint({
			image: getRandomEstasseguroAvatar(),
			imageHintUsed,
			hint: HINT_HTML,
			hintMarkHtml: SC2_HINT_MARK_HTML,
			color: "orange",
			big: false,
			themePopupClass: SC2_HINT_THEME_CLASS,
		});
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(SC2_QUIZ_HINT_COST_POINTS);
		}
	};

	function finishOrderQuestion(rubricPointsAwarded) {
		const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;
		const scoreFromRubric = rubricPointsToScoreDelta(
			rubricPointsAwarded,
			WEIGHTED_MAX_POINTS,
			WEIGHTED_MAX_GAME_DELTA
		);
		const attemptDelta = scoreFromRubric - hintThisAttempt;
		const newScore = pendingScore + attemptDelta;
		if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

		const capitalInicio = capitalAtQuizStartRef.current ?? 0;
		const netoEnEstaPregunta = newScore - capitalInicio;
		const rubricPass = rubricPointsAwarded >= QUIZ_RUBRIC_PASS_POINTS;
		const avatarUrl = rubricPass ? getRandomAciertoAvatar() : getRandomErrorAvatar();
		const outcomeStyle = rubricPass ? "correct" : "incorrect";

		modalQuestionResolution({
			correct: rubricPass,
			question: "Prioridad operativa",
			textHtml: `${buildQuizResultAvatarHtml(avatarUrl, outcomeStyle, "orange")}
              <div class="txt28 bold pt-3">${rubricPointsAwarded} puntos</div>
              <div class="txt22 mt-2">${rubricPass ? "Has acertado." : "No has acertado."}</div>
              <div class="txt20 mt-2">Puntos en el marcador (esta pregunta): <b>${netoEnEstaPregunta >= 0 ? "+" : ""}${netoEnEstaPregunta}</b> pt${
					hintThisAttempt ? ` (incluye −${hintThisAttempt} pt por pista)` : ""
				}</div>
              ${retroalimentacionBlockHtml}`,
			customConfirmButtonHtml: "Continuar",
			color: "orange",
			themePopupClass: SC2_SWAL_POPUP_CLASS,
		}).then(() => {
			submitAnswer(newScore, rubricPass, { rubricPoints: rubricPointsAwarded });
		});
	}

	function checkAnswer() {
		if (!allSlotsFilled) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${getRandomEstasseguroAvatar()}" alt="" class="estasseguro swal-estasseguro-img" /></div></div><div class="text-center txtTitulos mt-3 orange">Debes colocar las cuatro actuaciones en orden</div>`,
				themePopupClass: SC2_SWAL_POPUP_CLASS,
			});
			return;
		}

		modalConfirm({
			estasseguro: getRandomEstasseguroAvatar(),
			color: "orange",
			themePopupClass: SC2_SWAL_POPUP_CLASS,
		}).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const correct = isOrderCorrect(slots);

			if (correct) {
				const rubricPointsAwarded = attempt === 1 ? 100 : 50;
				finishOrderQuestion(rubricPointsAwarded);
				return;
			}

			if (attempt < NUMBER_ATTEMPTS) {
				modalQuestionResolution({
					correct: false,
					question: "Prioridad operativa",
					textHtml: `${buildQuizResultAvatarHtml(getRandomErrorAvatar(), "incorrect", "orange")}
                    <div class="txt28 bold pt-3">0 puntos</div>
                    <div class="txt22 mt-2">No has acertado. Tienes <b>un segundo intento</b>: revisa el orden y vuelve a comprobar.</div>`,
					customConfirmButtonHtml: "Continuar",
					color: "orange",
					themePopupClass: SC2_SWAL_POPUP_CLASS,
				}).then(() => {
					dispatchBoard({ type: "RESET" });
					setAttempt(2);
					setIsSubmitting(false);
				});
				return;
			}

			finishOrderQuestion(0);
		});
	}

	const remainingAttempts = NUMBER_ATTEMPTS - attempt + 1;

	return (
		<div className="start-game-page start-game-page--welcome sc2-context-page sc2-quiz-page sc2-quiz1-brand sc3-chrono-quiz sc2-priority-quiz">
			<div className="start-game-inner start-game-inner--prep5">
				<div className="sg5-scroll">
					<header className="sg5-hero">
						<div className="sc3-chrono-hero-head">
							<h1 className="sg5-title">Pregunta</h1>
							<span className="start-game-badge sg5-badge sc3-chrono-hero-badge">
								<span className="start-game-badge-dot" aria-hidden />
								Escenario 1 · Llegada y control de la situación
							</span>
						</div>
						<p className="sg5-lead sc2-quiz-stem sc3-chrono-stem">{QUESTION_STEM}</p>
					</header>

					<div className="sc2-quiz-toolbar sc3-chrono-toolbar">
						<p className="sc2-quiz-hint-line mb-0">
							<IconHandClick
								size={28}
								color="rgba(239, 239, 237, 0.85)"
								stroke={1.25}
								className="sc2-quiz-hand me-2"
							/>
							<span>
								Mantén pulsado una actividad, muévela y suéltala en la prioridad correspondiente (o en la
								zona inferior para devolverla). Tienes dos intentos; en el segundo, la puntuación máxima
								es 50 puntos. La pista resta {SC2_QUIZ_HINT_COST_POINTS} pt al marcador.
							</span>
						</p>
						<img
							src={remainingAttempts === 2 ? dosIntentos : unIntento}
							alt=""
							className="sc2-quiz-intentos"
						/>
					</div>

					<div className="sc3-chrono-layout">
						<div className="sc3-chrono-table" aria-label="Orden de prioridad">
							{Array.from({ length: Math.ceil(PRIORITY_ROWS.length / 2) }, (_, pairRowIndex) => {
								const start = pairRowIndex * 2;
								return (
									<div key={PRIORITY_ROWS[start].slotLabel} className="sc3-chrono-table-row-double">
										{[start, start + 1].map((slotIndex) => {
											if (slotIndex >= PRIORITY_ROWS.length) {
												return (
													<div
														key={`pad-${slotIndex}`}
														className="sc3-chrono-pair sc3-chrono-pair--placeholder"
														aria-hidden
													/>
												);
											}
											const row = PRIORITY_ROWS[slotIndex];
											const placedId = slots[slotIndex];
											return (
												<div key={row.activityId} className="sc3-chrono-pair" data-sc3-slot={slotIndex}>
													<div
														className="sc3-chrono-year sc2-priority-year"
														aria-label={`Prioridad ${row.slotLabel}`}
													>
														<span className="sc2-priority-year__word">Prioridad</span>
														<span className="sc2-priority-year__n">{row.slotLabel}</span>
													</div>
													<div
														className={`sc3-chrono-slot ${placedId ? "sc3-chrono-slot--filled" : "sc3-chrono-slot--empty"}`}
													>
														{placedId ? (
															<div
																tabIndex={0}
																className={`sc3-chrono-chip ${dragGhost?.activityId === placedId ? "sc3-chrono-chip--drag-source" : ""}`}
																onPointerDown={(e) =>
																	handleChipPointerDown(e, {
																		source: "slot",
																		activityId: placedId,
																		slotIndex,
																	})
																}
																onPointerMove={handleChipPointerMove}
																onPointerUp={handleChipPointerUpOrCancel}
																onPointerCancel={handleChipPointerUpOrCancel}
																onLostPointerCapture={(e) => {
																	if (dragSessionRef.current?.pointerId === e.pointerId) {
																		endPointerDrag(e.currentTarget, e.pointerId);
																	}
																}}
																onContextMenu={(ev) => ev.preventDefault()}
																title={ACTIVITY_TEXT[placedId]}
															>
																{ACTIVITY_TEXT[placedId]}
															</div>
														) : (
															<span className="sc3-chrono-slot-placeholder" aria-hidden="true">
																Soltar aquí
															</span>
														)}
													</div>
												</div>
											);
										})}
									</div>
								);
							})}
						</div>

						<div className="sc3-chrono-pool-wrap">
							<p className="sc3-chrono-pool-label mb-2">Actuaciones (arrastra desde aquí)</p>
							<div
								className="sc3-chrono-pool"
								data-sc3-drop="pool"
								aria-label="Actuaciones sin colocar"
							>
								{pool.length === 0 ? (
									<span className="sc3-chrono-pool-empty">Todas las fichas están colocadas</span>
								) : (
									pool.map((activityId) => (
										<div
											key={activityId}
											tabIndex={0}
											className={`sc3-chrono-chip ${dragGhost?.activityId === activityId ? "sc3-chrono-chip--drag-source" : ""}`}
											onPointerDown={(e) => handleChipPointerDown(e, { source: "pool", activityId })}
											onPointerMove={handleChipPointerMove}
											onPointerUp={handleChipPointerUpOrCancel}
											onPointerCancel={handleChipPointerUpOrCancel}
											onLostPointerCapture={(e) => {
												if (dragSessionRef.current?.pointerId === e.pointerId) {
													endPointerDrag(e.currentTarget, e.pointerId);
												}
											}}
											onContextMenu={(ev) => ev.preventDefault()}
											title={ACTIVITY_TEXT[activityId]}
										>
											{ACTIVITY_TEXT[activityId]}
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="sg5-actions sc2-quiz-footer">
					<div className="sc2-quiz-hint-wrap position-relative d-inline-block">
						<button
							type="button"
							className={`sc2-hint-trigger sc2-quiz-hint-btn ${hintUsed ? "used" : ""}`}
							onClick={useHint}
							aria-label={hintUsed ? "Pista usada" : "Usar pista"}
							title={
								hintUsed
									? `Pista usada (−${SC2_QUIZ_HINT_COST_POINTS} pt)`
									: `Usar pista (−${SC2_QUIZ_HINT_COST_POINTS} pt)`
							}
						>
							<HintMarkPista />
						</button>
						{hintUsed ? (
							<span className="badge-penalty sc2-quiz-hint-badge">−{SC2_QUIZ_HINT_COST_POINTS} pt</span>
						) : null}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="orange"
						loading={isSubmitting}
						bare
						className="sc2-quiz-check-wrap"
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />

			{typeof document !== "undefined" &&
				dragGhost &&
				createPortal(
					<div
						ref={ghostElRef}
						className="sc3-chrono-drag-ghost"
						role="presentation"
						aria-hidden="true"
					>
						{dragGhost.label}
					</div>,
					document.body
				)}
		</div>
	);
}

export default Scenario2Quiz2;
